import { useContext } from 'react';

import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { Box } from '@mui/material';

import { tokens } from '../../../../../../../theme/generated/tokens';
import { setRef } from '@cast/utils';

import { SettingsRow } from './SettingsRow';
import { SxProps, WithRef } from '../../../../../../../types';
import { ColumnsGroup } from '../_hooks/useSettingsDrawerState';
import { SettingsDrawerContext } from '../context';
import { SettingsRowModel } from '../types';

type Props = {
  rows: SettingsRowModel[];
  sx?: SxProps;
  group: ColumnsGroup;
};

export const DraggableRows = ({
  rows,
  sx,
  group,
  ref: outerRef,
}: WithRef<HTMLDivElement, Props>) => {
  const { reorderColumns } = useContext(SettingsDrawerContext);
  return (
    <DragDropContext
      onDragEnd={({ destination, draggableId }) => {
        if (!destination) {
          return;
        }
        reorderColumns({
          id: draggableId,
          newIndex: destination.index,
          group,
        });
      }}
    >
      <Droppable
        droppableId="table-settings"
        mode="standard"
        renderClone={(state, _, rubric) => {
          const style = state.draggableProps.style;
          if (style?.transform) {
            // Do not allow horizontal drag
            style.transform = style.transform.replace(
              /translate\(([^,]+),/,
              'translate(0,'
            );
          }
          return (
            <Box
              sx={{
                '& > div': {
                  boxShadow: tokens.theme.shadows.elevation.medium,
                  borderTop: `1px solid ${tokens.theme.border.selection.primary}`,
                  borderBottom: `1px solid ${tokens.theme.border.selection.primary}`,
                  backgroundColor: tokens.theme.bg.structural.surface,
                },
              }}
            >
              <SettingsRow
                dragHandleProps={state.dragHandleProps || undefined}
                dragContainerProps={state.draggableProps}
                dragRef={state.innerRef}
                column={rows[rubric.source.index]}
              />
            </Box>
          );
        }}
        ignoreContainerClipping
      >
        {(state) => (
          <Box
            ref={(ref: HTMLDivElement) => {
              state.innerRef(ref);
              setRef(outerRef, ref);
            }}
            sx={sx}
          >
            {rows.map((groupedProps, index) => {
              return (
                <Draggable
                  key={groupedProps.id}
                  draggableId={groupedProps.id}
                  index={index}
                  isDragDisabled={!groupedProps.visible}
                >
                  {(state) => {
                    return (
                      <SettingsRow
                        dragHandleProps={state.dragHandleProps || undefined}
                        dragContainerProps={state.draggableProps}
                        dragRef={state.innerRef}
                        column={groupedProps}
                      />
                    );
                  }}
                </Draggable>
              );
            })}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
};

DraggableRows.displayName = 'DraggableRows';
